import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Box, TableSortLabel, Checkbox } from '@mui/material';
import { useTheme } from '../../ThemeContext';

/**
 * StickyFlexTable - A robust, theme-aware, fixed-column table component with flexible config.
 *
 * @param {Object[]} columns - Array of column configs: { renderName, headerName, type, minWidth, fixed, align, renderCell, visible }
 * @param {Object[]} rows - Array of row data objects
 * @param {boolean} loading - Show loading state
 * @param {number} page - Current page (zero-based)
 * @param {number} rowsPerPage - Rows per page
 * @param {function} onPageChange - (event, newPage) => void
 * @param {function} onRowsPerPageChange - (event) => void
 * @param {function} [getRowKey] - Function to get unique row key (default: row._id or index)
 * @param {React.ReactNode} [toolbar] - Optional toolbar (e.g., add button, search)
 * @param {string[]} [visibleColumns] - Array of renderNames for visible columns
 * @param {function} [setVisibleColumns] - Setter for visibleColumns
 * @param {object} [sortModel] - { field, direction }
 * @param {function} [onSortModelChange] - Setter for sortModel
 * @param {function} [actionSelector] - Row action handler
 * @param {function} [rowSelector] - Row click handler
 * @param {number} [rowsCount] - Total rows (for server-side pagination)
 * @param {boolean} [isLoading] - Loading state
 * @param {boolean} [noCustomizeColumn] - Disable column customization
 * @param {string} [containerHeight] - Table container height
 * @param {string} [paginationMargin] - Pagination margin
 *
 * Usage:
 * <StickyFlexTable
 *   columns={[{ renderName: 'name', headerName: 'Name', fixed: true, renderCell: row => <b>{row.name}</b> }, ...]}
 *   rows={data}
 *   loading={loading}
 *   page={page}
 *   rowsPerPage={rowsPerPage}
 *   onPageChange={setPage}
 *   onRowsPerPageChange={setRowsPerPage}
 *   visibleColumns={visibleColumns}
 *   setVisibleColumns={setVisibleColumns}
 *   sortModel={sortModel}
 *   onSortModelChange={setSortModel}
 *   toolbar={<MyToolbar />}
 * />
 */
const StickyFlexTable = ({
  columns = [],
  rows = [],
  loading = false,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  getRowKey,
  toolbar,
  visibleColumns,
  setVisibleColumns,
  sortModel,
  onSortModelChange,
  actionSelector,
  rowSelector,
  rowsCount,
  isLoading,
  noCustomizeColumn,
  containerHeight = '60vh',
  paginationMargin = null,
  // Selection controls (optional and non-breaking)
  selectionEnabled = false,
  selectionKey = 'id',
  selectedKeys = [],
  onSelectedKeysChange,
  showSelectAll = true,
  isRowSelectable,
  checkboxColor, // New prop for custom checkbox color
}) => {
  const { currentTheme, themeMode } = useTheme();
  const isDark = themeMode === 'dark' || (currentTheme.palette && currentTheme.palette.mode === 'dark');
  const headerBg = currentTheme.callhistory?.headerBackground || (isDark ? '#242143' : '#f5f5f5');
  const headerText = currentTheme.text?.header || (isDark ? '#fff' : '#222');
  const cellBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const brandOrange = '#e63e2b';
  const checkboxAccentColor = checkboxColor || brandOrange; // Use custom color or fallback to brandOrange

  // Column visibility logic
  const allVisibleColumns = visibleColumns || columns.filter(col => col.visible !== false).map(col => col.renderName || col.id);
  const filteredColumns = columns.filter(col => allVisibleColumns.includes(col.renderName || col.id));

  // Sorting logic
  const handleSort = (col) => {
    if (!onSortModelChange) return;
    if (sortModel?.field === (col.renderName || col.id)) {
      onSortModelChange({
        field: col.renderName || col.id,
        direction: sortModel.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      onSortModelChange({ field: col.renderName || col.id, direction: 'asc' });
    }
  };

  // Column customization menu logic removed in this build (kept props for compatibility)

  const getKey = (row, idx) => (getRowKey ? getRowKey(row, idx) : row._id || idx);

  const selectedKeySet = React.useMemo(() => new Set(selectedKeys || []), [selectedKeys]);

  const resolveRowKey = React.useCallback((row, idx) => {
    const key = selectionKey && row && row[selectionKey] != null ? row[selectionKey] : (getRowKey ? getRowKey(row, idx) : row?._id ?? idx);
    return key;
  }, [selectionKey, getRowKey]);

  // Client-side pagination logic - only slice if rowsCount is not provided (server-side pagination)
  const paginatedRows = React.useMemo(() => {
    if (rowsCount) {
      // Server-side pagination - use all provided rows
      return rows;
    }
    // Client-side pagination - slice the rows
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return rows.slice(startIndex, endIndex);
  }, [rows, page, rowsPerPage, rowsCount]);

  const allPageKeys = React.useMemo(() => {
    if (!selectionEnabled) return [];
    const keys = [];
    paginatedRows.forEach((r, idx) => {
      const actualRowIndex = (page * rowsPerPage) + idx;
      const isSelectable = typeof isRowSelectable === 'function' ? isRowSelectable(r) : true;
      if (isSelectable) {
        keys.push(resolveRowKey(r, actualRowIndex));
      }
    });
    return keys;
  }, [paginatedRows, selectionEnabled, resolveRowKey, isRowSelectable, page, rowsPerPage]);

  const allPageSelected = selectionEnabled && allPageKeys.length > 0 && allPageKeys.every(k => selectedKeySet.has(k));
  const somePageSelected = selectionEnabled && allPageKeys.some(k => selectedKeySet.has(k)) && !allPageSelected;

  const handleToggleAll = () => {
    if (!onSelectedKeysChange) return;
    if (allPageSelected) {
      const next = (selectedKeys || []).filter(k => !allPageKeys.includes(k));
      onSelectedKeysChange(next);
    } else {
      const union = new Set([...(selectedKeys || []), ...allPageKeys]);
      onSelectedKeysChange(Array.from(union));
    }
  };

  const handleToggleOne = (key, disabled) => {
    if (!onSelectedKeysChange) return;
    if (disabled) return;
    const set = new Set(selectedKeys || []);
    if (set.has(key)) set.delete(key); else set.add(key);
    onSelectedKeysChange(Array.from(set));
  };

  return (
    <Paper sx={{
      width: '100%',
      background: currentTheme.callhistory.mainBackground,
      borderRadius: '16px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {toolbar && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pb: 3 }}>
          {toolbar}
          {/* {!noCustomizeColumn && setVisibleColumns && (
            <>
              <IconButton onClick={handleColumnMenuOpen} size="small" sx={{ ml: 2, color: currentTheme.text.primary }}>
                <span style={{ fontWeight: 600, fontSize: 16 }}>☰</span>
              </IconButton>
              <Menu anchorEl={anchorEl} open={columnMenuOpen} onClose={handleColumnMenuClose}>
                {columns.map(col => (
                  <MenuItem key={col.renderName || col.id} onClick={() => handleToggleColumn(col)}>
                    <Checkbox checked={allVisibleColumns.includes(col.renderName || col.id)} />
                    {col.headerName || col.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )} */}
        </Box>
      )}
      <TableContainer sx={{
        maxHeight: containerHeight || '65vh',
        maxWidth: '100%',
        overflow: 'auto',
        '@media (max-height: 800px)': {
          maxHeight: containerHeight || '62vh',
        },
        '@media (max-height: 736px)': {
          maxHeight: containerHeight || '40vh',
        },
        '@media (max-height: 700px)': {
          maxHeight: containerHeight || '55vh',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(120,120,120,0.25) transparent',
        '&::-webkit-scrollbar': {
          width: 6,
          height: 6,
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(120,120,120,0.25)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
      }}>
        <Table stickyHeader sx={{ tableLayout: 'fixed', margin: 0 }}>

          <TableHead sx={{ 
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}>
            <TableRow sx={{ background: currentTheme.loginPage.mainBackground }}>
              {selectionEnabled && (
                <TableCell
                  key="_select_all"
                  align="center"
                  sx={{
                    minWidth: 40,
                    width: 40,
                    background: headerBg,
                    color: headerText,
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 11,
                    borderBottom: cellBorder,
                    borderTopLeftRadius: '1vh',
                    borderBottomLeftRadius: '1vh',
                    fontWeight: 400,
                    fontSize: '0.90rem',
                    minHeight: '38px',
                    height: '38px',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                  }}
                >
                  {showSelectAll && (
                    <Checkbox
                      size="medium"
                      indeterminate={somePageSelected}
                      checked={allPageSelected}
                      onChange={handleToggleAll}
                      inputProps={{ 'aria-label': 'select all' }}
                      sx={{
                        color: checkboxAccentColor,
                        '&.Mui-checked': { color: brandOrange },
                        '&.MuiCheckbox-indeterminate': { color: brandOrange },
                        '& .MuiSvgIcon-root': { fontSize: 20 },
                      }}
                    />
                  )}
                </TableCell>
              )}
              {filteredColumns.map((col, colIdx) => (
                <TableCell
                  key={col.renderName || col.id}
                  align={col.align || 'left'}
                  sx={{
                    minWidth: col.minWidth,
                    width: col.width,          // ✅ NEW
                    maxWidth: col.maxWidth,    // ✅ optional
                    background: headerBg,
                    color: headerText,
                    position: 'sticky',
                    top: 0,
                    left: col.fixed && col.align !== 'right' ? 0 : undefined,
                    right: col.fixed && col.align === 'right' ? 0 : undefined,
                    zIndex: col.fixed ? 11 : 10,
                    fontWeight: 400,
                    cursor: col.sortable ? 'pointer' : undefined,
                    borderBottom: cellBorder,
                    borderTopLeftRadius: (colIdx === 0 && !selectionEnabled) ? '1vh' : undefined,
                    borderTopRightRadius: colIdx === filteredColumns.length - 1 ? '1vh' : undefined,
                    borderBottomLeftRadius: (colIdx === 0 && !selectionEnabled) ? '1vh' : undefined,
                    borderBottomRightRadius: colIdx === filteredColumns.length - 1 ? '1vh' : undefined,
                    fontSize: '0.90rem',
                    minHeight: '38px',
                    height: '38px',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                  }}
                  sortDirection={sortModel?.field === (col.renderName || col.id) ? sortModel.direction : false}
                  onClick={col.sortable && onSortModelChange ? () => handleSort(col) : undefined}
                >
                  {col.sortable && onSortModelChange ? (
                    <TableSortLabel
                      active={sortModel?.field === (col.renderName || col.id)}
                      direction={sortModel?.direction || 'asc'}
                    >
                      {col.headerName || col.label}
                    </TableSortLabel>
                  ) : (
                    col.headerName || col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading || loading ? (
              <TableRow sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}>
                <TableCell colSpan={filteredColumns.length + (selectionEnabled ? 1 : 0)} align="center" sx={{ padding: '5rem 2.5rem', height: '25rem' }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '18.75rem'
                  }}>
                    <img
                      src={
                        themeMode === "dark"
                          ? "/icons/loader_white.gif"
                          : "/icons/loader_black.gif"
                      }
                      alt="Loading..."
                      style={{ width: "9.375rem", height: "9.375rem" }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}>
                <TableCell colSpan={filteredColumns.length + (selectionEnabled ? 1 : 0)} align="center" sx={{ color: currentTheme.text.primary, fontSize: '.9rem' }}>
                  No data found.
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, idx) => {
                // Calculate the actual row index based on current page
                const actualRowIndex = (page * rowsPerPage) + idx;
                return (
                  <TableRow
                    key={getKey(row, actualRowIndex)}
                    onClick={rowSelector ? () => rowSelector(row) : undefined}
                    sx={{
                      cursor: rowSelector ? 'pointer' : undefined,
                      '&:hover': {
                        backgroundColor: 'inherit !important'
                      }
                    }}
                  >
                    {selectionEnabled && (
                      <TableCell
                        key={`_select_${actualRowIndex}`}
                        align="center"
                        sx={{
                          position: 'sticky',
                          left: 0,
                          background: currentTheme.callhistory.mainBackground,
                          zIndex: 2,
                          borderBottom: cellBorder,
                          width: 40,
                        }}
                      >
                        {(() => {
                          const key = resolveRowKey(row, actualRowIndex);
                          const disabled = typeof isRowSelectable === 'function' ? !isRowSelectable(row) : false;
                          return (
                            <Checkbox
                              size="medium"
                              disabled={disabled}
                              checked={selectedKeySet.has(key)}
                              onChange={() => handleToggleOne(key, disabled)}
                              inputProps={{ 'aria-label': 'select row' }}
                              sx={{
                                color: checkboxAccentColor,
                                '&.Mui-checked': { color: brandOrange },
                                '&.MuiCheckbox-indeterminate': { color: brandOrange },
                                '& .MuiSvgIcon-root': { fontSize: 20 },
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                    )}
                    {filteredColumns.map((col, colIdx) => (
                      <TableCell
                        key={col.renderName || col.id}
                        align={col.align || 'left'}
                        sx={{
                          width: col.width,          // ✅ NEW
                          maxWidth: col.maxWidth,    // ✅ optional
                          minWidth: col.minWidth,
                          position: col.fixed ? 'sticky' : 'static',
                          left: col.fixed && col.align !== 'right' ? 0 : undefined,
                          right: col.fixed && col.align === 'right' ? 0 : undefined,
                          background: col.fixed ? currentTheme.callhistory.mainBackground : undefined,
                          zIndex: col.fixed ? 2 : 1,
                          color: currentTheme.text.primary,
                          fontWeight: col.fixed ? 500 : 200,
                          borderBottom: cellBorder,
                          fontSize: '.9rem', // FEAT: FONT SIZE FOR TABLE CELLS
                        }}
                      >
                        {col.renderCell ? col.renderCell(row, actualRowIndex) : row[col.renderName || col.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={rowsCount || rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        SelectProps={{
          sx: {
            backgroundColor: themeMode === 'dark' ? '#151138' : '#ffffff',
            color: themeMode === 'dark' ? '#ffffff' : '#000000',
            '& .MuiSvgIcon-root': {
              color: themeMode === 'dark' ? '#ffffff' : '#555555 !important',
            },
            '& .MuiSelect-icon': {
              color: themeMode === 'dark' ? '#ffffff' : '#555555 !important',
            },
            '& svg': {
              color: themeMode === 'dark' ? '#ffffff' : '#555555 !important',
              fill: themeMode === 'dark' ? '#ffffff' : '#555555 !important',
            },
          },
          MenuProps: {
            PaperProps: {
              sx: {
                backgroundColor: themeMode === 'dark' ? '#151138' : '#ffffff',
                color: themeMode === 'dark' ? '#ffffff' : '#000000',
              },
            },
          },
        }}
        sx={{
          color: currentTheme.text.primary, 
          background: currentTheme.callhistory.mainBackground, 
          mt: paginationMargin || 0,
          flexShrink: 0,
          '.MuiTablePagination-selectLabel, .MuiTablePagination-input': {
            color: themeMode === 'dark' ? '#ffffff' : '#000000',
          },
          // Pagination navigation arrows
          '.MuiTablePagination-actions': {
            '& .MuiIconButton-root': {
              color: themeMode === 'dark' ? '#ffffff' : '#222222',
            },
            '& .MuiIconButton-root.Mui-disabled': {
              color: themeMode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
            },
          },
          // Display text (e.g., "1-10 of 100")
          '.MuiTablePagination-displayedRows': {
            color: themeMode === 'dark' ? '#ffffff' : '#000000',
          },
        }}
      />
    </Paper>
  );
};

export default StickyFlexTable; 
